<section class="flex h-full w-full flex-col overflow-hidden bg-white text-slate-900" role="dialog">
  <header class="flex shrink-0 items-center gap-3 border-b !border-slate-100 px-4 py-3">
    <div class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-orange-50 text-orange-600">
      <app-icon>payments</app-icon>
    </div>
    <h2 class="min-w-0 flex-1 truncate !m-0 !text-base !font-bold !leading-6">
      {{ (payment ? 'board_payments.form.title_edit' : 'board_payments.form.title_new') | translate }}
    </h2>
    <app-button btnType="icon" [disabled]="saving" [ariaLabel]="'close' | translate" (onChangeClick)="close.emit()">
      <app-icon>close</app-icon>
    </app-button>
  </header>

  @if (loading) {
    <div class="flex flex-1 flex-col gap-4 px-4 py-4">
      <div class="h-10 animate-pulse rounded-lg bg-slate-100"></div>
      <div class="h-10 animate-pulse rounded-lg bg-slate-100"></div>
      <div class="h-10 animate-pulse rounded-lg bg-slate-100"></div>
      <div class="h-24 animate-pulse rounded-lg bg-slate-100"></div>
    </div>
  } @else if (loadFailed) {
    <div class="flex flex-1 flex-col items-center justify-center gap-3 p-5 text-center">
      <app-icon class="!text-[32px] text-slate-300">error_outline</app-icon>
      <p class="!m-0 text-sm text-slate-600">{{ 'board_payments.form.load_failed' | translate }}</p>
      <app-button btnType="stroked" (onChangeClick)="load()">{{ 'board_payments.form.retry' | translate }}</app-button>
    </div>
  } @else {
    <form class="crm-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4" [attr.inert]="saving || submittedReceipt ? '' : null" (ngSubmit)="submit()">
      <app-select
        [formControl]="form.controls.payment_type_id"
        [disabled]="form.controls.payment_type_id.disabled || saving"
        [options]="typeOptions"
        optionLabel="name"
        optionValue="id"
        [required]="true"
        [label]="'board_payments.fields.payment_type' | translate"
      />
      @if (!typeOptions.length) {
        <p class="!-mt-2 !mb-0 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-700">
          {{ 'board_payments.form.no_types' | translate }}
        </p>
      }
      @if (serviceOptions.length || form.controls.payment_service_id.value) {
      <app-select
        [formControl]="form.controls.payment_service_id"
        [disabled]="form.controls.payment_service_id.disabled || saving"
        [options]="serviceOptions"
        optionLabel="name"
        optionValue="id"
        [showClear]="true"
        [label]="'board_payments.fields.payment_service' | translate"
        [placeholder]="'board_payments.form.no_service' | translate"
      />
      }
      @if (manualProviderPayment) {
        <p class="!-mt-2 !mb-0 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">{{ 'board_payments.form.manual_provider_hint' | translate }}</p>
      }
      @if (invoiceService) {
        <div class="flex flex-col gap-4 rounded-lg border !border-slate-200 bg-slate-50/60 p-3">
          <div class="flex items-start justify-between gap-3">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="text-sm font-medium text-slate-700">{{ 'board_payments.invoice.issue' | translate }}</span>
              <p class="!m-0 text-xs leading-5 text-slate-400">
                {{
                  (isInvoiceCurrency ? 'board_payments.invoice.issue_hint' : 'board_payments.invoice.uah_only')
                    | translate
                }}
              </p>
            </div>
            <app-toggle class="shrink-0" [formControl]="form.controls.invoice_enabled" [disabled]="!isInvoiceCurrency">
              <span class="sr-only">{{ 'board_payments.invoice.issue' | translate }}</span>
            </app-toggle>
          </div>
          @if (form.controls.invoice_enabled.value && isInvoiceCurrency) {
            <app-input
              [formControl]="form.controls.invoice.controls.legal_name"
              [label]="'board_payments.fields.legal_name' | translate"
              [required]="true"
            />
            <app-input
              [formControl]="form.controls.invoice.controls.tax_id"
              [label]="'board_payments.fields.tax_id' | translate"
              [required]="true"
              [pKeyFilter]="digitsOnly"
            />
            <app-input
              [formControl]="form.controls.invoice.controls.iban"
              [label]="'board_payments.fields.iban' | translate"
              [required]="true"
            />
            <app-textarea
              [formControl]="form.controls.invoice.controls.purpose"
              [label]="'board_payments.fields.purpose' | translate"
              [maxLength]="purposeMaxLength"
            />
            <p class="!m-0 text-xs leading-5 text-slate-400">{{ 'board_payments.invoice.details_hint' | translate }}</p>
          }
        </div>
      }
      <app-input
        [formControl]="form.controls.amount"
        [disabled]="form.controls.amount.disabled || saving"
        [label]="('board_payments.fields.amount' | translate) + ', ' + currencySymbol"
        [required]="true"
      />
      <app-select
        [formControl]="form.controls.status"
        [options]="statusOptions"
        optionLabel="label"
        optionValue="value"
        [isTranslate]="true"
        [label]="'board_payments.fields.status' | translate"
      />

      <div class="flex flex-col gap-1">
        <span class="text-xs">{{ 'board_payments.fields.paid_at' | translate }}</span>
        <div class="flex items-center gap-2">
          <p-datepicker
            class="min-w-0 flex-1"
            [fluid]="true"
            dataType="string"
            dateFormat="yy-mm-dd"
            [showButtonBar]="true"
            appendTo="body"
            panelStyleClass="board-v3-datepicker-panel"
            inputStyleClass="h-10 w-full rounded-lg border !border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:!border-orange-400"
            [placeholder]="'date' | translate"
            [ariaLabel]="'date' | translate"
            [formControl]="form.controls.paid_date"
          />
          <p-datepicker
            class="w-28 shrink-0"
            dataType="string"
            [timeOnly]="true"
            hourFormat="24"
            [stepMinute]="1"
            appendTo="body"
            panelStyleClass="board-v3-datepicker-panel"
            inputStyleClass="h-10 w-full rounded-lg border !border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:!border-orange-400 disabled:cursor-not-allowed disabled:bg-slate-50"
            [placeholder]="'time_label' | translate"
            [ariaLabel]="'time_label' | translate"
            [formControl]="form.controls.paid_time"
          />
        </div>
        <p class="!m-0 text-xs leading-5 text-slate-400">{{ 'board_payments.form.paid_at_hint' | translate }}</p>
      </div>

      @if (connected && form.controls.status.value === 'paid') {
        <section class="flex flex-col gap-4 rounded-xl border !border-orange-200 bg-orange-50/40 p-4">
          <h3 class="!m-0 !text-sm !font-semibold">{{ 'prro.receipt_settings' | translate }}</h3>
          <p class="!m-0 text-xs text-slate-600">{{ 'prro.settlement_hint' | translate }}</p>
          @if (settlementAmountError) {
            <p class="!m-0 rounded-lg bg-red-50 p-3 text-xs text-red-700">{{ settlementAmountError | translate }}</p>
          }
          @if (settlementBlocked) {
            <p class="!m-0 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">{{ 'prro.settlement.' + receipt?.settlement_status | translate }}</p>
            @if (receipt?.settlement_status === 'advance_pending') {
              <app-button btnType="stroked" [disabled]="previewLoading" (onChangeClick)="loadPreview()">{{ 'prro.refresh' | translate }}</app-button>
            }
          }
          @if (fiscalDenied) {
            <p class="text-sm text-amber-700">{{ 'prro.denied' | translate }}</p>
          }
          <app-select
            [formControl]="cashRegisterId"
            [options]="registers"
            optionLabel="name"
            optionValue="id"
            [required]="true"
            [label]="'prro.register' | translate"
          />
          @if (!registers.length) {
            <p class="text-sm text-amber-700">{{ 'prro.empty_registers' | translate }}</p>
          }
          @if (previewLoading) {
            <p class="text-sm text-slate-500">{{ 'prro.loading' | translate }}</p>
          }
          @if (previewFailed) {
            <app-button btnType="stroked" (onChangeClick)="loadPreview()">{{ 'prro.retry' | translate }}</app-button>
          }
          @if (receipt) {
            @if (previewChanged) {
              <p class="!m-0 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">{{ 'prro.preview_changed' | translate }}</p>
            }
            <div class="flex flex-col gap-2 rounded-lg bg-white p-3 text-sm">
              @for (row of receipt.rows; track $index) {
                <div class="border-b !border-slate-100 py-2">
                  <strong>{{ row.name }}</strong>
                  <div class="flex justify-between text-slate-600">
                    <span>{{ row.cnt }} × {{ row.price | number: '1.2-2' }}</span
                    ><span>{{ (row.cost ?? row.cnt * row.price) - (row.disc ?? 0) | number: '1.2-2' }} UAH</span>
                  </div>
                  <div class="text-xs text-slate-500">
                    {{ 'prro.tax_group' | translate }}: {{ 'prro.tax_groups.' + row.taxgrp | translate }}
                  </div>
                  @if (row.disc) {
                    <div class="text-xs text-slate-500">{{ 'prro.discount' | translate }}: {{ row.disc }}</div>
                  }
                </div>
              }
              <div class="flex justify-between font-bold">
                <span>{{ 'prro.total' | translate }}</span
                ><span>{{ receipt.total | number: '1.2-2' }} UAH</span>
              </div>
              <div class="mt-2 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3 text-xs">
                <span class="text-slate-500">{{ 'prro.settlement.paid' | translate }}</span><span class="text-right">{{ receipt.paid_total ?? 0 | number: '1.2-2' }} UAH</span>
                <span class="text-slate-500">{{ 'prro.settlement.remaining' | translate }}</span><span class="text-right">{{ receipt.remaining ?? receipt.total | number: '1.2-2' }} UAH</span>
                @if (settlementKind; as kind) {
                  <strong>{{ 'prro.settlement.' + kind | translate }}</strong><strong class="text-right">{{ form.controls.amount.value }} UAH</strong>
                }
              </div>
              <p class="!m-0 text-xs text-slate-500">{{ 'prro.products_hint' | translate }}</p>
            </div>
          }
          @if (needsCardMetadata) {
            <p class="!m-0 text-xs text-slate-500">{{ 'prro.metadata_hint' | translate }}</p>
            @for (field of metadataFields; track field) {
              <app-input
                [(ngModel)]="metadata[field]"
                [ngModelOptions]="{ standalone: true }"
                [label]="'prro.metadata.' + field | translate"
                [required]="field !== 'purchase_dt'"
              />
            }
          }
          @if (chats.length) {
            <app-select
              [formControl]="chatId"
              [options]="chatOptions"
              optionLabel="name"
              optionValue="id"
              [required]="true"
              [label]="'prro.destination_chat' | translate"
            />
            <p class="!m-0 text-xs text-slate-500">{{ 'prro.delivery_hint' | translate }}</p>
          } @else {
            <p class="!m-0 text-xs text-slate-500">{{ 'prro.no_chat_hint' | translate }}</p>
          }
        </section>
      }
      <app-textarea
        [formControl]="form.controls.comment"
        [disabled]="form.controls.comment.disabled || saving"
        [label]="'board_payments.fields.comment' | translate"
        [maxLength]="commentMaxLength"
      />
    </form>

    <footer class="flex shrink-0 gap-2 border-t !border-slate-100 px-4 py-3">
      <app-button
        btnType="stroked"
        class="flex-1"
        [customClass]="'w-full !h-10 !rounded-lg'"
        [disabled]="saving"
        (onChangeClick)="close.emit()"
      >
        {{ 'cancel' | translate }}
      </app-button>
      <app-button
        class="flex-1"
        [customClass]="'w-full !h-10 !rounded-lg'"
        [disabled]="
          saving ||
          (!submittedReceipt && connected &&
            form.controls.status.value === 'paid' &&
            (!cashRegisterId.value || !receipt || previewLoading || previewFailed || settlementBlocked || !!settlementAmountError || (!!chats.length && !chatId.value)))
        "
        (onChangeClick)="submit()"
      >
        {{
          (saving
            ? (connected && form.controls.status.value === 'paid' ? 'prro.waiting_for_receipt' : 'saving')
            : submittedReceipt ? 'prro.retry'
            : connected && form.controls.status.value === 'paid'
              ? 'prro.confirm_receipt'
              : payment
                ? 'save'
                : 'create'
          ) | translate
        }}
      </app-button>
    </footer>
    @if (submittedReceipt) {
      <p class="!m-0 border-t !border-slate-100 px-4 py-3 text-xs text-slate-500">{{ 'prro.payment_pending_receipt' | translate }}</p>
    }
  }
</section>
